import { CalendarOutlined } from "@ant-design/icons";
import { Breadcrumb, Col, Divider, Row, Space, Tabs, Typography } from "antd";
import { useLoaderData, useNavigate } from "react-router-dom";
import { LeaseDetailsPageLoaderType } from "./loaders/LeaseDetailsPageLoader";
import { LeaseHistory } from "./LeaseHistory";
import {
  breadcrumbHomeItem,
  breadcrumbItem,
  CustomTextField,
  LeaseStatusTag,
  MoneyText,
} from "~/components";
import { FormattedHoursTag } from "~/components/DateAndTime";
import * as APITypes from "~/API";
import { getRouteWithParams } from "~/utils";
import { useAppTranslation } from "~/hooks/useAppTranslation";

/** Normalize manager info to a consistent display shape (User has companyName, Organization has name). */
function getManagerDisplay(
  managerInfo: APITypes.User | APITypes.Organization | undefined
) {
  if (!managerInfo) {
    return {
      companyName: "",
      phoneNumber: "",
      email: "",
      officeAddress: "",
      workingHours: null as Array<string | null> | null | undefined,
    };
  }
  if (managerInfo.__typename === "Organization") {
    return {
      companyName: managerInfo.name ?? "",
      phoneNumber: "",
      email: "",
      officeAddress: "",
      workingHours: null as Array<string | null> | null | undefined,
    };
  }
  return {
    companyName: managerInfo.companyName ?? "",
    phoneNumber: managerInfo.phoneNumber ?? "",
    email: managerInfo.email ?? "",
    officeAddress: managerInfo.officeAddress ?? "",
    workingHours: managerInfo.workingHours ?? null,
  };
}

export const LeaseDetailsPage = () => {
  const loaderData = useLoaderData() as LeaseDetailsPageLoaderType;
  const { t } = useAppTranslation();
  const manager = getManagerDisplay(
    loaderData.formattedLease.managerInfo
  );
  const disable =
    loaderData.formattedLease.leaseInfo.status === "Expired" ||
    loaderData.formattedLease.leaseInfo.status === "Rejected" ||
    loaderData.formattedLease.leaseInfo.status === "Terminated" ||
    loaderData.formattedLease.leaseInfo.status === "Terminating";

  const navigate = useNavigate();

  return (
    <>
      <Breadcrumb
        separator=">"
        items={[
          breadcrumbHomeItem,
          breadcrumbItem(
            <>
              {t("Lease_Info")}: <b>{loaderData.formattedLease.leaseInfo.id}</b>
            </>
          ),
        ]}
      />

      <Typography.Title level={1} style={{ marginTop: "2rem" }}>
        {loaderData.formattedLease.entityType === APITypes.EntityType.Unit
          ? t("Unit")
          : t("Building")}
        : {loaderData.entityDetails.name}
      </Typography.Title>

      <Tabs
        onChange={(key) => {
          switch (key) {
            case "lease_details":
            case "lease_history": {
              navigate(
                getRouteWithParams("leaseDetails", {
                  leaseID: loaderData.formattedLease.leaseInfo.id,
                })
              );
              break;
            }
          }
        }}
        items={[
          {
            key: "lease_history",
            label: t("Lease_History"),
            children: (
              <>
                <LeaseHistory
                  lease={loaderData.formattedLease}
                  disable={disable}
                />
              </>
            ),
          },
          {
            key: "lease_details",
            label: t("Lease_Info"),
            children: (
              <>
                <Row gutter={12} style={{ marginTop: "2rem" }}>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Lease_Status")}
                      </Typography.Text>
                      <LeaseStatusTag
                        status={loaderData.formattedLease.leaseInfo.status}
                      />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Type")}
                      </Typography.Text>
                      <CustomTextField
                        value={loaderData.formattedLease.leaseInfo?.type || ""}
                      />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Start_Date")}
                      </Typography.Text>
                      <CustomTextField
                        value={
                          loaderData.formattedLease.leaseInfo?.startDate || ""
                        }
                        onEmpty="n/a"
                        prefix={<CalendarOutlined />}
                      />
                    </Space>
                  </Col>
                </Row>

                <Row gutter={12} style={{ marginTop: "2rem" }}>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("End_Date")}
                      </Typography.Text>
                      <CustomTextField
                        value={
                          loaderData.formattedLease.leaseInfo?.endDate || ""
                        }
                        onEmpty="n/a"
                        prefix={<CalendarOutlined />}
                      />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Marked_Date")}
                      </Typography.Text>
                      <CustomTextField
                        value={
                          loaderData.formattedLease.leaseInfo?.markedDate || ""
                        }
                        onEmpty="n/a"
                        prefix={<CalendarOutlined />}
                      />
                    </Space>
                  </Col>
                </Row>

                <Divider />
                <Typography.Title level={5}>
                  {t("Manager_Info")}
                </Typography.Title>

                <Row gutter={12} style={{ marginTop: "2rem" }}>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Company_Name")}
                      </Typography.Text>
                      <CustomTextField value={manager.companyName} />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Phone_Number")}
                      </Typography.Text>
                      <CustomTextField value={manager.phoneNumber} />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Email_Address")}
                      </Typography.Text>
                      <CustomTextField value={manager.email} />
                    </Space>
                  </Col>
                </Row>

                <Row gutter={12} style={{ marginTop: "2rem" }}>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Office_Address")}
                      </Typography.Text>
                      <CustomTextField value={manager.officeAddress} />
                    </Space>
                  </Col>
                  <Col span={16}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Working_Hours")}:
                      </Typography.Text>
                      {manager.workingHours
                        ? FormattedHoursTag(manager.workingHours)
                        : ""}
                    </Space>
                  </Col>
                </Row>

                <Divider />
                <Typography.Title level={5}>
                  {t("Entity_Info")}
                </Typography.Title>

                <Row gutter={12} style={{ marginTop: "2rem" }}>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Entity_Type")}
                      </Typography.Text>
                      <CustomTextField
                        value={
                          loaderData.formattedLease.leaseInfo?.entityType || ""
                        }
                      />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Name")}
                      </Typography.Text>
                      <CustomTextField
                        value={
                          loaderData.formattedLease.entityDetails?.name || ""
                        }
                        link={`/${loaderData.formattedLease.leaseInfo?.entityType}/${loaderData.formattedLease.entityDetails?.id}`}
                        newPage={true}
                      />
                    </Space>
                  </Col>
                  <Col span={8}>
                    <Space direction="vertical">
                      <Typography.Text strong style={{ marginBottom: 5 }}>
                        {t("Rent")}
                      </Typography.Text>
                      <MoneyText
                        value={
                          loaderData.formattedLease.entityDetails?.rent || ""
                        }
                      />
                    </Space>
                  </Col>
                </Row>
              </>
            ),
          },
        ]}
      />
    </>
  );
};
